import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, RefreshCw, Send } from 'lucide-react';
import { apiBase, authHeaders } from '../utils/authHeaders';
import { commandId, useCkScope } from './CkOperationsShared';
import { clearPendingCapture, loadPendingCapture, persistPendingCapture, tokenFingerprint } from './operandiCaptureQueue';
import OperandiHumanWorkflow from './OperandiHumanWorkflow';
import OperandiProcessVisibility from './OperandiProcessVisibility';
import OperandiProjectionHistory from './OperandiProjectionHistory';
import OperandiSourceMetrics from './OperandiSourceMetrics';
import OperandiDeliveryStatus from './OperandiDeliveryStatus';
import OperandiAdvisoryGrants from './OperandiAdvisoryGrants';
import { clearPendingMappingReview, clearPendingWorkflow, loadPendingMappingReview, loadPendingWorkflow } from './operandiWorkflowQueue';

const api = `${apiBase()}/api/operandi`;

export default function OperandiHumanContext() {
  const scope = useCkScope();
  const [searchParams] = useSearchParams();
  const requestedRunId = searchParams.get('runId') || '';
  const { orgId, locationId, token, department } = scope;
  const [runs, setRuns] = useState([]);
  const [runId, setRunId] = useState('');
  const runIdRef = useRef(runId);
  runIdRef.current = runId;
  const [projection, setProjection] = useState(null);
  const [projectionScope, setProjectionScope] = useState('');
  const projectionKey = JSON.stringify([orgId, locationId, token, runId]);
  const visibleProjection = projectionScope === projectionKey ? projection : null;
  const runsController = useRef(null);
  const [projectionLoading, setProjectionLoading] = useState(false);
  const [workflowPending, setWorkflowPending] = useState(false);
  const [historyPending, setHistoryPending] = useState(false);
  const [projectionRevision, setProjectionRevision] = useState(0);
  const workflowRecorded = useCallback(() => setProjectionRevision(value => value + 1), []);
  const [sourceKind, setSourceKind] = useState('VERBAL_REPORT');
  const [statement, setStatement] = useState('');
  const [documents, setDocuments] = useState([]);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState([]);
  const [evidencePreview, setEvidencePreview] = useState(null);
  const previewController = useRef(null);
  const previewUrl = useRef(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [activeCommandId, setActiveCommandId] = useState(commandId);
  const [pendingCapture, setPendingCapture] = useState(null);
  const [confirmingId, setConfirmingId] = useState(null);
  const [error, setError] = useState('');
  const headers = useMemo(
    () => authHeaders({ orgId, locationId, token, department }),
    [orgId, locationId, token, department],
  );

  const closeEvidencePreview = useCallback(() => {
    previewController.current?.abort();
    previewController.current = null;
    if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
    previewUrl.current = null;
    setEvidencePreview(null);
  }, []);

  useEffect(() => closeEvidencePreview, [orgId, locationId, token, closeEvidencePreview]);

  const openEvidencePreview = async (doc) => {
    previewController.current?.abort();
    if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
    previewUrl.current = null;
    const controller = new AbortController();
    previewController.current = controller;
    setEvidencePreview({ filename: doc.filename, loading: true });
    setError('');
    try {
      const response = await fetch(`${apiBase()}/api/documents/${encodeURIComponent(doc.id)}/view?proxy=true`, {
        headers, signal: controller.signal,
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.detail || 'Evidence could not be opened. Access may have changed.');
      }
      const contentType = (response.headers.get('Content-Type') || '').split(';')[0].trim().toLowerCase();
      const kind = contentType === 'application/pdf' ? 'pdf'
        : ['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(contentType) ? 'image' : null;
      if (!kind) throw new Error('Inline preview is available only for PDF and common image evidence.');
      const url = URL.createObjectURL(await response.blob());
      if (controller.signal.aborted) { URL.revokeObjectURL(url); return; }
      previewUrl.current = url;
      setEvidencePreview({ filename: doc.filename, kind, url, loading: false });
    } catch (e) {
      if (!controller.signal.aborted) setEvidencePreview({ filename: doc.filename, loading: false, error: e.message });
    }
  };

  useEffect(() => {
    setPendingCapture(null); setActiveCommandId(commandId()); setStatement('');
    setSelectedDocumentIds([]); setRunId(''); setProjection(null); setWorkflowPending(false); setHistoryPending(false);
    if (!orgId || !locationId || !token) { clearPendingCapture(); clearPendingWorkflow(); clearPendingMappingReview(); return undefined; }
    let cancelled = false;
    loadPendingCapture({ orgId, locationId, token }).then(capture => {
      if (cancelled || !capture) return;
      setPendingCapture(capture); setActiveCommandId(capture.command_id);
      setStatement(capture.statement); setSourceKind(capture.source_kind);
      setSelectedDocumentIds(capture.evidence_refs.map(ref => ref.id));
      setRunId(capture.run_id);
    });
    return () => { cancelled = true; };
  }, [orgId, locationId, token]);

  const loadRuns = useCallback(async () => {
    if (!orgId || !locationId || !token) return;
    runsController.current?.abort();
    const controller = new AbortController();
    runsController.current = controller;
    setBusy(true); setError('');
    try {
      const response = await fetch(`${api}/discovery-runs`, { headers, signal: controller.signal });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.detail || 'Could not load discovery runs.');
      if (controller.signal.aborted) return;
      const records = Array.isArray(payload.items) ? payload.items : [];
      const savedWorkflow = await loadPendingWorkflow({ orgId, locationId, token });
      const savedReview = await loadPendingMappingReview({ orgId, locationId, token });
      if (controller.signal.aborted) return;
      setRuns(records);
      const linkedRun = records.find(run => run.id === requestedRunId);
      if (linkedRun && runIdRef.current !== linkedRun.id)
        setRunId(linkedRun.id);
      else if (!records.some(run => run.id === runIdRef.current))
        setRunId(records.find(run => run.id === (savedWorkflow?.runId || savedReview?.runId))?.id || records[0]?.id || '');
    } catch (e) { if (!controller.signal.aborted) setError(e.message); }
    finally { if (!controller.signal.aborted) setBusy(false); }
  }, [orgId, locationId, token, headers, requestedRunId]);

  useEffect(() => { loadRuns(); return () => runsController.current?.abort(); }, [loadRuns]);

  useEffect(() => {
    if (!orgId || !locationId || !token) return;
    const controller = new AbortController();
    setDocuments([]); setSelectedDocumentIds([]);
    (async () => {
      try {
        const response = await fetch(`${api}/evidence-documents`, { headers, signal: controller.signal });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.detail || 'Could not load evidence documents.');
        if (!controller.signal.aborted) setDocuments(Array.isArray(payload.items) ? payload.items : []);
      } catch (e) { if (!controller.signal.aborted) setError(e.message); }
    })();
    return () => controller.abort();
  }, [orgId, locationId, token, headers]);

  useEffect(() => {
    if (!runId) { setProjection(null); setProjectionLoading(false); return; }
    const controller = new AbortController();
    setProjection(null); setProjectionLoading(true);
    (async () => {
      try {
        const response = await fetch(`${api}/discovery-runs/${encodeURIComponent(runId)}/projection`, { headers, signal: controller.signal });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.detail || 'Could not load this run.');
        if (!controller.signal.aborted) { setProjection(payload); setProjectionScope(projectionKey); }
      } catch (e) { if (!controller.signal.aborted) setError(e.message); }
      finally { if (!controller.signal.aborted) setProjectionLoading(false); }
    })();
    return () => controller.abort();
  }, [runId, scope.orgId, scope.locationId, scope.token, headers, projectionRevision, projectionKey]);

  const submit = async (event) => {
    event.preventDefault(); setBusy(true); setError(''); setMessage('');
    const refs = documents.filter(doc => selectedDocumentIds.includes(doc.id))
      .map(({ id, version, hash, kind }) => ({ id, version, hash, kind }));
    const capture = pendingCapture || { command_id: activeCommandId, run_id: runId,
      source_kind: sourceKind, statement, evidence_refs: refs };
    setPendingCapture(capture);
    try {
      await persistPendingCapture(capture, { orgId, locationId, token });
      const response = await fetch(`${api}/discovery-runs/${encodeURIComponent(capture.run_id)}/context`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(capture),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.detail || 'Could not record context.');
      clearPendingCapture(); setStatement(''); setSelectedDocumentIds([]); setPendingCapture(null); setActiveCommandId(commandId()); setMessage('Recorded as UNVERIFIED human context.');
      setProjectionRevision(value => value + 1);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const confirmStatement = async (item) => {
    if (!item?.can_confirm || !runId || busy) return;
    setBusy(true); setConfirmingId(item.id); setError(''); setMessage('');
    const key = `operandi-human-context-confirm:${orgId}:${locationId}:${runId}:${item.id}`;
    let pending;
    try {
      const fingerprint = await tokenFingerprint(token);
      if (!fingerprint) throw new Error('Secure retry storage is unavailable. Confirmation was not sent.');
      pending = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (!pending || pending.capture_id !== item.id || pending.run_id !== runId ||
          pending.token_fingerprint !== fingerprint || !pending.command_id) {
        pending = { capture_id: item.id, run_id: runId, expected_version: 1,
          command_id: commandId(), token_fingerprint: fingerprint };
        sessionStorage.setItem(key, JSON.stringify(pending));
      }
      const response = await fetch(`${api}/discovery-runs/${encodeURIComponent(runId)}/context/${encodeURIComponent(item.id)}/confirm`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ command_id: pending.command_id, expected_version: pending.expected_version }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.detail || 'Could not confirm this statement. Retry with the same command.');
      if (payload?.confirmation?.status !== 'CONFIRMED' || payload.confirmation.classification !== 'UNVERIFIED' || payload.confirmation.no_action_authority !== true)
        throw new Error('The confirmation response failed its safety checks. Retry the same command.');
      sessionStorage.removeItem(key);
      setMessage('Your statement is author-confirmed. Its classification remains UNVERIFIED.');
      setProjectionRevision(value => value + 1);
    } catch (e) {
      if (e instanceof SyntaxError) setError('Could not read the pending confirmation. Refresh and try again.');
      else setError(e.message);
    } finally { setBusy(false); setConfirmingId(null); }
  };

  if (!scope.token || !scope.locationId) return <main className="mx-auto max-w-5xl p-6"><h1 className="text-2xl font-semibold">Process discovery</h1><p className="mt-3">Select a site and sign in to view its discovery runs.</p></main>;

  return <main className="mx-auto max-w-5xl p-4 sm:p-6">
    <header className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 pb-4">
      <div><h1 className="text-2xl font-semibold text-gray-900">Process discovery</h1><p className="mt-1 text-sm text-gray-600">Evidence-backed process view and human observations</p></div>
      <button type="button" onClick={loadRuns} disabled={busy} className="inline-flex min-h-10 items-center gap-2 rounded border border-gray-300 px-3 text-sm"><RefreshCw size={16} /> Refresh</button>
    </header>
    <div className="mt-4 flex gap-3 border-l-4 border-amber-500 bg-amber-50 p-3 text-sm text-amber-950" role="note"><AlertTriangle size={18} className="shrink-0" /><p>Human-entered context is unverified. Do not enter personal or special-category data. Evidence references must point to records you are authorized to access.</p></div>
    {error && <p role="alert" className="mt-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">{error}</p>}
    {message && <p role="status" className="mt-4 rounded border border-green-300 bg-green-50 p-3 text-sm text-green-900">{message}</p>}
    <section className="mt-6 grid gap-6 lg:grid-cols-[minmax(15rem,0.8fr)_minmax(0,1.2fr)]">
      <div><label className="block text-sm font-medium" htmlFor="discovery-run">Discovery run</label><select id="discovery-run" className="mt-1 min-h-11 w-full rounded border border-gray-300 bg-white px-3" value={runId} disabled={Boolean(pendingCapture) || workflowPending || historyPending} onChange={e => setRunId(e.target.value)}><option value="">Choose run</option>{runs.map(run => <option key={run.id} value={run.id}>{run.aggregate_type} {run.aggregate_id} · {run.status}</option>)}</select>
        <div className="mt-4 border-t border-gray-200 pt-4"><h2 className="font-semibold">Process evidence</h2><OperandiProcessVisibility projection={visibleProjection?.projection} loading={projectionLoading} onConfirm={confirmStatement} confirmingId={confirmingId} /></div>
      </div>
      <form onSubmit={submit} className="border-t border-gray-200 pt-4 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
        <h2 className="font-semibold">Record a human observation</h2><label className="mt-4 block text-sm font-medium" htmlFor="context-source">Source</label><select id="context-source" value={sourceKind} disabled={Boolean(pendingCapture)} onChange={e => setSourceKind(e.target.value)} className="mt-1 min-h-11 w-full rounded border border-gray-300 bg-white px-3"><option value="VERBAL_REPORT">Verbal report</option><option value="PAPER_NOTE">Paper note</option><option value="PHYSICAL_WALKTHROUGH">Physical walkthrough</option><option value="MANUAL_ENTRY">Manual entry</option></select>
        <label className="mt-4 block text-sm font-medium" htmlFor="context-statement">Observation</label><textarea id="context-statement" required maxLength={2000} value={statement} disabled={Boolean(pendingCapture)} onChange={e => setStatement(e.target.value)} className="mt-1 min-h-32 w-full rounded border border-gray-300 p-3" placeholder="Describe the process or exception observed" />
        <fieldset className="mt-4"><legend className="text-sm font-medium">Site evidence documents</legend>
          <div className="mt-1 max-h-40 overflow-auto border border-gray-300 p-2 text-sm">
            {documents.length ? documents.map(doc => <div key={doc.id} className="flex min-h-10 items-center gap-2">
              <input aria-label={`Attach ${doc.filename}`} type="checkbox" checked={selectedDocumentIds.includes(doc.id)} disabled={Boolean(pendingCapture) || (!selectedDocumentIds.includes(doc.id) && selectedDocumentIds.length >= 10)} onChange={e => setSelectedDocumentIds(ids => e.target.checked ? [...ids, doc.id] : ids.filter(id => id !== doc.id))} />
              <span className="min-w-0 flex-1 break-all">{doc.filename}</span>
              <button type="button" className="shrink-0 rounded border border-gray-300 px-2 py-1 text-xs underline" onClick={() => openEvidencePreview(doc)}>View</button>
            </div>) : <p className="text-gray-600">No accessible site documents.</p>}
          </div>
        </fieldset>
        <button type="submit" disabled={busy || !runId || !statement.trim()} className="mt-4 inline-flex min-h-11 items-center gap-2 rounded bg-gray-900 px-4 text-sm font-medium text-white disabled:opacity-50"><Send size={16} /> {pendingCapture ? 'Retry same observation' : 'Record observation'}</button>
        {pendingCapture && <button type="button" disabled={busy} onClick={() => { clearPendingCapture(); setPendingCapture(null); setActiveCommandId(commandId()); setError(''); }} className="ml-3 min-h-11 text-sm underline">Discard pending retry</button>}
      </form>
    </section>
    {evidencePreview && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 sm:p-6" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) closeEvidencePreview(); }}>
      <section role="dialog" aria-modal="true" aria-labelledby="evidence-preview-title" onKeyDown={event => { if (event.key === 'Escape') closeEvidencePreview(); }} className="flex max-h-full w-full max-w-5xl flex-col overflow-hidden rounded bg-white shadow-xl">
        <header className="flex items-center justify-between gap-3 border-b border-gray-200 p-3 sm:p-4">
          <h2 id="evidence-preview-title" className="min-w-0 break-all text-base font-semibold">{evidencePreview.filename}</h2>
          <button type="button" autoFocus onClick={closeEvidencePreview} className="min-h-10 shrink-0 rounded border border-gray-300 px-3 text-sm">Close</button>
        </header>
        {evidencePreview.loading ? <p role="status" className="p-6">Loading evidence…</p>
          : evidencePreview.error ? <p role="alert" className="p-6 text-red-800">{evidencePreview.error}</p>
            : evidencePreview.kind === 'image' ? <div className="overflow-auto p-3"><img className="mx-auto max-h-[75vh] max-w-full object-contain" src={evidencePreview.url} alt={evidencePreview.filename} /></div>
              : <iframe className="min-h-[65vh] w-full flex-1" title={`Evidence preview: ${evidencePreview.filename}`} src={evidencePreview.url} />}
      </section>
    </div>}
    {runId && <OperandiHumanWorkflow key={`${orgId}:${locationId}:${token}:${runId}`} runId={runId} scope={scope} headers={headers}
      contexts={visibleProjection?.projection?.human_context || []} documents={documents}
      onRecorded={workflowRecorded} onPendingChange={setWorkflowPending} />}
    {runId && <OperandiProjectionHistory key={`history:${orgId}:${locationId}:${token}:${runId}`}
      runId={runId} scope={scope} headers={headers} snapshotId={visibleProjection?.snapshot?.id} onRecorded={workflowRecorded}
      onPendingChange={setHistoryPending} />}
    <OperandiSourceMetrics key={`metrics:${orgId}:${locationId}:${token}`} headers={headers}/>
    <OperandiDeliveryStatus key={`delivery:${orgId}:${locationId}:${token}`} headers={headers}/>
    <OperandiAdvisoryGrants key={`grants:${orgId}:${locationId}:${token}`} headers={headers} scope={scope}/>
  </main>;
}
